import { loadSpeakerChoice, preferLabelledDevices, saveSpeakerChoice } from './audioOutput'

export type DeviceKind = 'audioinput' | 'videoinput' | 'audiooutput'

export type DeviceLists = Record<DeviceKind, MediaDeviceInfo[]>

const INPUT_STORE_KEYS: Record<'audioinput' | 'videoinput', string> = {
  audioinput: 'ischool-meet:microphone',
  videoinput: 'ischool-meet:camera',
}

const EMPTY_LISTS: DeviceLists = {
  audioinput: [],
  videoinput: [],
  audiooutput: [],
}

/** One browser enumeration split into the three device kinds. */
export async function listDevices(): Promise<DeviceLists> {
  try {
    if (typeof navigator === 'undefined' || typeof navigator.mediaDevices?.enumerateDevices !== 'function') {
      return EMPTY_LISTS
    }
    const all = await navigator.mediaDevices.enumerateDevices()
    return {
      audioinput: preferLabelledDevices(all.filter((device) => device.kind === 'audioinput')),
      videoinput: preferLabelledDevices(all.filter((device) => device.kind === 'videoinput')),
      audiooutput: preferLabelledDevices(all.filter((device) => device.kind === 'audiooutput')),
    }
  } catch {
    return EMPTY_LISTS
  }
}

export function loadDeviceChoice(kind: DeviceKind): string {
  if (kind === 'audiooutput') return loadSpeakerChoice()
  try {
    return localStorage.getItem(INPUT_STORE_KEYS[kind]) ?? ''
  } catch {
    return ''
  }
}

export function saveDeviceChoice(kind: DeviceKind, deviceId: string): void {
  if (kind === 'audiooutput') {
    saveSpeakerChoice(deviceId)
    return
  }
  try {
    if (deviceId) localStorage.setItem(INPUT_STORE_KEYS[kind], deviceId)
    else localStorage.removeItem(INPUT_STORE_KEYS[kind])
  } catch {
    /* private mode keeps the choice for this lesson only */
  }
}

let switchInFlight = false

/** Shared with recovery so one physical device change cannot acquire twice. */
export function isDeviceSwitchInFlight(): boolean {
  return switchInFlight
}

/**
 * Holds the recovery mutex through the SDK's old-track teardown tail. The extra
 * second prevents the stopped track and the matching devicechange burst from
 * racing a fresh getUserMedia call.
 */
export async function withDeviceSwitchInFlight<T>(operation: () => Promise<T>): Promise<T | undefined> {
  if (switchInFlight) return undefined
  switchInFlight = true
  try {
    return await operation()
  } finally {
    await new Promise<void>((resolve) => window.setTimeout(resolve, 1_000))
    switchInFlight = false
  }
}
